import { log, warn } from '../utils/logger'
import {
    Modal,
    TextInput,
    Select,
    NumberInput,
    Button,
    Stack,
    Group,
    Text,
    Alert,
    Loader,
    ActionIcon,
    Tooltip,
    Box
} from '@mantine/core'
import {
    IconSearch,
    IconExternalLink,
    IconAlertCircle,
    IconCheck
} from '@tabler/icons-react'
import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { gamesApi, storesApi, pricesApi } from '../services/api'
import type { Price } from '../types'

// Local storage key for remembering the last selected store
const LAST_SELECTED_STORE_KEY = 'spellen-land-last-selected-store'

// Helper functions for local storage
const getLastSelectedStore = (): string | null => {
    try {
        return localStorage.getItem(LAST_SELECTED_STORE_KEY)
    } catch (error) {
        warn('Failed to get last selected store from localStorage:', error)
        return null
    }
}

const setLastSelectedStore = (storeId: string): void => {
    try {
        localStorage.setItem(LAST_SELECTED_STORE_KEY, storeId)
    } catch (error) {
        warn('Failed to save last selected store to localStorage:', error)
    }
}

interface AddPriceModalProps {
    opened: boolean
    onClose: () => void
    preselectedGameId?: string
    preselectedPlatformId?: string
    priceToEdit?: Price | null
}

export const AddPriceModal: React.FC<AddPriceModalProps> = ({
    opened,
    onClose,
    preselectedGameId,
    preselectedPlatformId,
    priceToEdit
}) => {
    const queryClient = useQueryClient()

    // Form state - initialize with preselected values or priceToEdit
    const [selectedGame, setSelectedGame] = useState<string | null>(priceToEdit ? priceToEdit.gameId : (preselectedGameId || null))
    const [selectedPlatform, setSelectedPlatform] = useState<string | null>(priceToEdit ? priceToEdit.platformId : (preselectedPlatformId || null))
    const [selectedStore, setSelectedStore] = useState<string | null>(priceToEdit ? priceToEdit.storeId : getLastSelectedStore())
    const [amount, setAmount] = useState<number | ''>(priceToEdit ? priceToEdit.amount : '')
    const [currency, setCurrency] = useState(priceToEdit ? priceToEdit.currency : 'EUR')
    const [storeUrl, setStoreUrl] = useState(priceToEdit ? priceToEdit.storeUrl || '' : '')
    const [shipping, setShipping] = useState<number | ''>(priceToEdit ? priceToEdit.shipping : '')
    const [total, setTotal] = useState<number | ''>(priceToEdit ? priceToEdit.total : '')

    // Search state
    const [gameSearch, setGameSearch] = useState('')
    const [debouncedGameSearch, setDebouncedGameSearch] = useState('')

    // Debounce game search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedGameSearch(gameSearch)
        }, 300)

        return () => clearTimeout(timer)
    }, [gameSearch])

    // Reset form when modal closes
    useEffect(() => {
        if (!opened) {
            setSelectedGame(null)
            setSelectedPlatform(null)
            // Don't reset selectedStore - keep it for next time
            setAmount('')
            setCurrency('EUR')
            setStoreUrl('')
            setShipping('')
            setTotal('')
            setGameSearch('')
            setDebouncedGameSearch('')
        }
    }, [opened])

    // Set preselected values or priceToEdit when props change or modal opens
    useEffect(() => {
        if (opened) {
            if (priceToEdit) {
                // Only set values if they're different to avoid unnecessary re-renders
                setSelectedGame(priceToEdit.gameId)
                setSelectedPlatform(priceToEdit.platformId)
                setSelectedStore(priceToEdit.storeId)
                setAmount(priceToEdit.amount)
                setCurrency(priceToEdit.currency)
                setStoreUrl(priceToEdit.storeUrl || '')
                setShipping(priceToEdit.shipping)
                setTotal(priceToEdit.total)
            } else {
                setSelectedGame(preselectedGameId || null)
                setSelectedPlatform(preselectedPlatformId || null)
                // Load the last selected store from localStorage for new prices
                setSelectedStore(getLastSelectedStore())
                setAmount('')
                setCurrency('EUR')
                setStoreUrl('')
                setShipping('')
                setTotal('')
            }
        }
    }, [opened, preselectedGameId, preselectedPlatformId, priceToEdit?.id, priceToEdit]) // Use priceToEdit.id instead of the whole object

    // Clear platform when game changes (unless it's a valid platform for the new game)
    useEffect(() => {
        if (selectedGame && !preselectedPlatformId && !priceToEdit) {
            setSelectedPlatform(null)
        }
    }, [selectedGame, preselectedPlatformId, priceToEdit])

    // Queries
    const { data: gameOptions, isLoading: gamesLoading } = useQuery({
        queryKey: ['games-search', debouncedGameSearch],
        queryFn: () => gamesApi.searchGames(debouncedGameSearch),
        select: (response) => {
            const gamePlatforms = response.data.data || []
            // Group by game and get unique games
            const gamesMap = new Map()
            gamePlatforms.forEach((gamePlatform) => {
                if (!gamesMap.has(gamePlatform.game.id)) {
                    gamesMap.set(gamePlatform.game.id, {
                        value: gamePlatform.game.id,
                        label: gamePlatform.game.title,
                        game: gamePlatform.game,
                        platforms: []
                    })
                }
                gamesMap.get(gamePlatform.game.id).platforms.push(gamePlatform.platform)
            })
            const result = Array.from(gamesMap.values())
            return result
        },
        enabled: debouncedGameSearch.length >= 2,
        staleTime: 30000,
    })

    // Get the selected game data from a separate query to ensure it's always available
    const { data: selectedGameData } = useQuery({
        queryKey: ['game-by-id', selectedGame || preselectedGameId],
        queryFn: () => gamesApi.getGameById((selectedGame || preselectedGameId)!),
        select: (response) => {
            const game = response.data.data
            if (!game) return null

            // Get platforms for this game
            return {
                value: game.id,
                label: game.title,
                game: game,
                platforms: game.platforms.map(gp => gp.platform)
            }
        },
        enabled: !!(selectedGame || preselectedGameId),
    })

    // Update search input when selected game data is available (for pre-selected games)
    useEffect(() => {
        if (selectedGame && selectedGameData && !gameSearch) {
            setGameSearch(selectedGameData.label)
        }
    }, [selectedGame, selectedGameData, gameSearch])

    // Clear search when a game is manually selected (but not when pre-selected)
    useEffect(() => {
        if (selectedGame && gameSearch && selectedGameData && gameSearch !== selectedGameData.label) {
            setGameSearch('')
            setDebouncedGameSearch('')
        }
    }, [selectedGame, gameSearch, selectedGameData])

    // Combine search results with selected game data
    const allGameOptions = useMemo(() => {
        const options = [...(gameOptions || [])]
        if (selectedGameData && !options.find(g => g.value === selectedGameData.value)) {
            options.push(selectedGameData)
        }
        return options
    }, [gameOptions, selectedGameData])

    // Get platforms for selected game
    const currentSelectedGameData = allGameOptions?.find(game => game.value === selectedGame)
    const availablePlatforms = useMemo(() => currentSelectedGameData?.platforms || [], [currentSelectedGameData])

    // Auto-select platform if there's only one available
    useEffect(() => {
        if (selectedGame && availablePlatforms.length === 1 && !selectedPlatform && !priceToEdit) {
            setSelectedPlatform(availablePlatforms[0].id)
        }
    }, [selectedGame, availablePlatforms, selectedPlatform, priceToEdit])

    const { data: stores } = useQuery({
        queryKey: ['stores-all'],
        queryFn: () => storesApi.getStores({ page: 1, limit: 100 }),
        select: (response) => response.data.data,
    })

    // Create or update price mutation
    const createOrUpdatePriceMutation = useMutation({
        mutationFn: (data: { gameId: string; platformId: string; storeId: string; amount: number; currency: string; storeUrl?: string; shipping?: number; total?: number }) => {
            log('🚀 Mutation called with data:', data)
            log('🚀 priceToEdit:', priceToEdit)
            if (priceToEdit) {
                log('🚀 Updating price with ID:', priceToEdit.id)
                return pricesApi.updatePrice(priceToEdit.id, data)
            } else {
                log('🚀 Creating new price')
                return pricesApi.createPrice(data)
            }
        },
        onSuccess: (_, variables) => {
            // Save the selected store to localStorage when creating a new price
            if (!priceToEdit && variables.storeId) {
                setLastSelectedStore(variables.storeId)
            }
            // Invalidate all queries and specifically search cache
            queryClient.invalidateQueries()
            queryClient.invalidateQueries({ queryKey: ['search-games'] })
            onClose()
        },
        onError: (error) => {
            console.error('❌ Price mutation failed:', error)
        }
    })

    // Handle game selection
    const handleGameSelect = (value: string | null) => {
        setSelectedGame(value)
        // Don't clear search immediately - let the component handle it
    }

    // Handle form submission
    const handleSubmit = () => {
        if (!selectedGame || !selectedPlatform || !selectedStore || amount === '' || amount <= 0) {
            return
        }

        createOrUpdatePriceMutation.mutate({
            gameId: selectedGame,
            platformId: selectedPlatform,
            storeId: selectedStore,
            amount: Number(amount),
            currency,
            storeUrl: storeUrl || undefined,
            shipping: shipping !== '' ? Number(shipping) : undefined,
            total: total !== '' ? Number(total) : undefined,
        })
    }

    // Preview URL
    const handlePreviewUrl = () => {
        if (storeUrl) {
            window.open(storeUrl, '_blank')
        }
    }

    const isFormValid = selectedGame && selectedPlatform && selectedStore && amount !== '' && amount > 0

    // Debug selected game data
    useEffect(() => {
        log('🎮 Selected game changed to:', selectedGame)
        log('📊 Selected game data:', selectedGameData)
        log('📋 All game options:', allGameOptions)
        log('🎯 Current selected game data:', currentSelectedGameData)
    }, [selectedGame, selectedGameData, allGameOptions, currentSelectedGameData])

    // Debug platform selection
    useEffect(() => {
        log('🎮 Selected platform changed to:', selectedPlatform)
        log('📊 Price to edit:', priceToEdit)
        log('📋 Available platforms:', availablePlatforms)
    }, [selectedPlatform, priceToEdit, availablePlatforms])

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={priceToEdit ? "Edit Price" : "Add New Price"}
            size="lg"
            closeOnClickOutside={false}
        >
            <Stack gap="md">
                {/* Game Selection */}
                <Box>
                    <Text size="sm" fw={500} mb="xs">
                        Game
                    </Text>
                    <Select
                        placeholder="Search for a game..."
                        searchable
                        searchValue={gameSearch}
                        onSearchChange={setGameSearch}
                        data={allGameOptions || []}
                        value={selectedGame}
                        onChange={handleGameSelect}
                        leftSection={<IconSearch size={16} />}
                        rightSection={gamesLoading ? <Loader size="xs" /> : null}
                        clearable
                    />
                </Box>

                {/* Platform Selection */}
                <Box>
                    <Text size="sm" fw={500} mb="xs">
                        Platform
                    </Text>
                    <Select
                        placeholder={selectedGame ? "Select a platform" : "Select a game first"}
                        data={availablePlatforms.map((platform: { id: string; name: string }) => ({
                            value: platform.id,
                            label: platform.name
                        }))}
                        value={selectedPlatform}
                        onChange={setSelectedPlatform}
                        searchable
                        clearable
                        disabled={!selectedGame}
                    />
                </Box>

                {/* Store Selection */}
                <Box>
                    <Text size="sm" fw={500} mb="xs">
                        Store
                    </Text>
                    <Select
                        placeholder="Select a store"
                        data={stores?.map(store => ({
                            value: store.id,
                            label: `${store.name} (${store.country})`
                        })) || []}
                        value={selectedStore}
                        onChange={setSelectedStore}
                        searchable
                        clearable
                    />
                </Box>

                {/* Price and Currency */}
                <Group grow>
                    <Box>
                        <Text size="sm" fw={500} mb="xs">
                            Price
                        </Text>
                        <NumberInput
                            placeholder="0.00"
                            value={amount}
                            onChange={(value) => setAmount(typeof value === 'number' ? value : '')}
                            min={0}
                            step={0.01}
                            decimalScale={2}
                            leftSection="€"
                        />
                    </Box>
                    <Box>
                        <Text size="sm" fw={500} mb="xs">
                            Currency
                        </Text>
                        <Select
                            data={[
                                { value: 'EUR', label: 'EUR (€)' },
                                { value: 'USD', label: 'USD ($)' },
                                { value: 'GBP', label: 'GBP (£)' },
                            ]}
                            value={currency}
                            onChange={(value) => setCurrency(value || 'EUR')}
                        />
                    </Box>
                </Group>

                {/* Shipping and Total */}
                <Group grow>
                    <Box>
                        <Text size="sm" fw={500} mb="xs">
                            Shipping (Optional)
                        </Text>
                        <NumberInput
                            placeholder="0.00"
                            value={shipping}
                            onChange={(value) => setShipping(typeof value === 'number' ? value : '')}
                            min={0}
                            step={0.01}
                            decimalScale={2}
                            leftSection="€"
                        />
                    </Box>
                    <Box>
                        <Text size="sm" fw={500} mb="xs">
                            Total (Optional)
                        </Text>
                        <NumberInput
                            placeholder="0.00"
                            value={total}
                            onChange={(value) => setTotal(typeof value === 'number' ? value : '')}
                            min={0}
                            step={0.01}
                            decimalScale={2}
                            leftSection="€"
                        />
                    </Box>
                </Group>

                {/* Store URL */}
                <Box>
                    <Text size="sm" fw={500} mb="xs">
                        Store URL (Optional)
                    </Text>
                    <Group gap="xs">
                        <TextInput
                            placeholder="https://store.example.com/game"
                            value={storeUrl}
                            onChange={(event) => setStoreUrl(event.target.value)}
                            style={{ flex: 1 }}
                        />
                        <Tooltip label="Preview URL">
                            <ActionIcon
                                variant="subtle"
                                onClick={handlePreviewUrl}
                                disabled={!storeUrl}
                            >
                                <IconExternalLink size={16} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                </Box>

                {/* Error handling */}
                {createOrUpdatePriceMutation.error && (
                    <Alert icon={<IconAlertCircle size={16} />} color="red">
                        Failed to {priceToEdit ? 'update' : 'create'} price: {createOrUpdatePriceMutation.error.message}
                    </Alert>
                )}

                {/* Success message */}
                {createOrUpdatePriceMutation.isSuccess && (
                    <Alert icon={<IconCheck size={16} />} color="green">
                        Price {priceToEdit ? 'updated' : 'created'} successfully!
                    </Alert>
                )}

                {/* Action buttons */}
                <Group justify="flex-end" mt="md">
                    <Button variant="subtle" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        loading={createOrUpdatePriceMutation.isPending}
                        disabled={!isFormValid}
                    >
                        {priceToEdit ? 'Update Price' : 'Create Price'}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
} 